import { useQuery } from "@tanstack/react-query";
import type React from "react";

import type { AIAnalysisModalState } from "@/components/AIAnalysisModal/AIAnalysisModal";
import { AIAnalysisModal } from "@/components/AIAnalysisModal/AIAnalysisModal";
import type { ModalBaseProps } from "@/components/Modal/Modal";
import { asUtc } from "@/helpers/date";
import { useAnalyticsQueries } from "@/queries/analytics";
import { useTranslation } from "@/translations";

type FahHistoryQuestionGroupAnswerSentimentModalProps = ModalBaseProps & {
  questionId: string;
  startDate?: Date;
  endDate?: Date;
};

export function FahHistoryQuestionGroupAnswerSentimentModal({
  questionId,
  startDate,
  endDate,
  isOpened,
  onOpenChange,
}: FahHistoryQuestionGroupAnswerSentimentModalProps): React.ReactNode {
  const { t } = useTranslation();

  const analyticsQueries = useAnalyticsQueries();
  const { data, isPending, isError } = useQuery({
    ...analyticsQueries.getFahAiQuestionSummary({
      questionId,
      startDate: startDate ? asUtc(startDate).toISOString() : undefined,
      endDate: endDate ? asUtc(endDate).toISOString() : undefined,
    }),
    enabled: isOpened,
  });

  let state: AIAnalysisModalState = "loading";
  if (!isPending && !isError && data) {
    state = "success";
  } else if (!isPending && isError) {
    state = "error";
  } else if (!isPending && !isError && !data) {
    state = "unavailable";
  }

  return (
    <AIAnalysisModal
      title={t("component.fah-history.sentiment-modal.title")}
      data={{
        sentiment: data?.sentiment || 0,
        summary: data?.summary || "",
        suggestion: data?.suggestion,
        hashtags: data?.hashtags,
      }}
      config={{
        success: {
          title: t("page.automated-surveys.sentiment.modal.title"),
        },
        unavailable: {
          title: t("page.automated-surveys.sentiment.no-data.notification"),
        },
        error: {
          title: t("page.automated-surveys.sentiment.failed.notification"),
        },
      }}
      {...{ state, isOpened, onOpenChange }}
    />
  );
}
